import { Button } from '@/components/Button';
import { CheckboxOption, Dropdown, RadioOption } from '@/components/Dropdown';
import { Flex } from '@/components/Grid';
import { useRouteLoading } from '@/hooks/useRouteLoading';
import { useAccount } from '@/resource/account';
import {
  AdFilterType,
  AdType,
  AdCategoryCountItemType as CategoryType,
  SortKey,
  distances,
} from '@/resource/ad';
import { LocationItemType, getLocations } from '@/resource/location';
import { decodePathSegment } from '@/util/encodePath';
import { routeToPath } from '@/util/routeToPath';
import { useRouter } from 'next/router';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import { IconButton } from '../IconButton';
import { Criteria } from './Criteria';
import styles from './Filter.module.scss';
import { Sidebar } from './Sidebar';
import { SortDropdown } from './SortDropdown';

type Props = {
  categories: CategoryType[];
  filter: AdFilterType;
  adType: AdType;
};

const postalCodePattern = /^\d{4}$/;

export function Filter({ categories: apiCategories, filter, adType }: Props) {
  const overviewType = `market_${adType}s` as const;

  const router = useRouter();
  const loading = useRouteLoading();
  const { profile } = useAccount();

  const profileLocation = profile?.location?.data;
  const profilePostalCode = profileLocation?.postal_code;

  const [categories, setCategories] = useState<CategoryType[]>(apiCategories);
  const [distance, setDistance] = useState<number>(filter.distance || -1);
  const [keyword, setKeyword] = useState<string>(filter.keyword || '');
  const [sort, setSort] = useState<SortKey>(filter.sort || 'recent');
  const [postalCode, setPostalCode] = useState(
    String(filter.postal_code || profilePostalCode || '')
  );

  function canSearch(value: string) {
    return value.trim().length > 1;
  }

  const [location, setLocation] = useState<LocationItemType | undefined>(
    profileLocation
  );
  useEffect(() => {
    if (postalCodePattern.test(postalCode)) {
      getLocations({ postalCode: Number(postalCode) }).then((data) => {
        // Also allow postal codes of inactive provinces here.
        setLocation(data[0]);
      });
    } else {
      setLocation(undefined);
    }
  }, [postalCode]);
  const hasLocation = location != null;

  // eslint-disable-next-line
  const { sort: _, ...criteria } = filter;
  const hasCriteria = Object.keys(criteria).length > 0;

  // Update state when filters change.
  useEffect(() => setCategories(apiCategories), [apiCategories]);
  useEffect(
    () => setPostalCode(String(filter.postal_code || profilePostalCode || '')),
    [filter.postal_code, profilePostalCode]
  );
  useEffect(() => setSort(filter.sort || 'recent'), [filter.sort]);
  useEffect(() => setDistance(filter.distance || -1), [filter.distance]);
  useEffect(
    () => setKeyword(decodePathSegment(filter.keyword || '')),
    [filter.keyword]
  );

  // Total number of ads
  const totalAdCount = categories.reduce((sum, item) => sum + item.count, 0);

  // Selected categories are managed through local state
  // They are used to update the checkboxes before the filters are applied.
  const selectedCategories = categories.filter((category) => category.checked);
  const selectedCategoryNames = selectedCategories.map(
    (category) => category.name
  );
  const selectedCategoryCount = selectedCategories.length;

  // Keep track if all categories are checked (to update the first checkbox)
  const allCategoriesChecked = categories.every(({ checked }) => checked);
  const [useAllCategories, setUseAllCategories] =
    useState(allCategoriesChecked);
  useEffect(() => {
    setUseAllCategories(allCategoriesChecked);
  }, [allCategoriesChecked]);

  function toggleCategory(category: CategoryType) {
    setCategories((categories) =>
      categories.map(
        (item): CategoryType =>
          category.id == item.id ? { ...item, checked: !item.checked } : item
      )
    );
  }

  const selectedCategoryIds = selectedCategories.map((category) => category.id);
  const categoryIds = useAllCategories ? [] : selectedCategoryIds;

  // Applied categories are managed by the server.
  const appliedCategories = categories.filter((category) =>
    filter.category_ids?.includes(category.id)
  );

  // Toggle mobile filters.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(sidebarRef, () => setSidebarOpen(false));

  useEffect(() => {
    if (sidebarOpen && sidebarRef.current) {
      sidebarRef.current.focus();
    }
  }, [sidebarOpen]);

  function filterBy({
    sort,
    categoryIds,
    distance,
    postalCode,
    keyword,
  }: {
    sort: SortKey;
    categoryIds: number[];
    distance: number;
    postalCode: string;
    keyword: string;
  }) {
    setSidebarOpen(false);

    router.push(
      routeToPath({
        type: overviewType,
        filter: {
          distance,
          keyword,
          category_ids: categoryIds,
          sort,
          postal_code: postalCodePattern.test(postalCode)
            ? Number(postalCode)
            : undefined,
        },
      })
    );
  }

  function getCategoryLabel() {
    if (useAllCategories) {
      return 'Alle categorieën';
    }
    if (selectedCategoryCount === 0) {
      return 'Geen selectie';
    }
    if (selectedCategoryCount > 3) {
      return `${selectedCategoryCount} categorieën`;
    }
    return selectedCategoryNames.join(', ');
  }

  const searchInput = (
    <input
      type="text"
      placeholder="Zoekterm"
      value={keyword}
      maxLength={100}
      onChange={(e) => {
        const { value } = e.target;

        if (canSearch(value) === false && sort === 'relevant') {
          setSort('recent');
        }

        setKeyword(value);
      }}
    />
  );

  const submitButton = (
    <Button
      type="submit"
      theme="secondary"
      disabled={loading || selectedCategoryCount === 0}
    >
      Zoek
    </Button>
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    filterBy({
      sort: sort || 'recent',
      categoryIds,
      distance,
      postalCode,
      keyword,
    });
  }

  return (
    <>
      <form className={styles.FilterMobile} onSubmit={onSubmit}>
        <Flex>
          {searchInput}
          {submitButton}
        </Flex>

        <Flex justify="space-between" align="center">
          <IconButton
            title="Toon filtermogelijkheden"
            icon="filter"
            size={5}
            iconSize={3}
            onClick={() => setSidebarOpen(true)}
          />

          <Flex align="center">
            <label htmlFor="sort_options_mobile">Sorteer op</label>

            <SortDropdown
              id="sort_options_mobile"
              {...{
                sort,
                categoryIds,
                distance,
                postalCode,
                filterBy,
                keyword,
                hasLocation,
              }}
            />
          </Flex>
        </Flex>
      </form>

      <form className={styles.Filter} onSubmit={onSubmit}>
        <div className={styles.FilterInner}>
          {searchInput}

          <Dropdown label={getCategoryLabel()}>
            <CheckboxOption
              label="Alle categorieën"
              count={totalAdCount}
              checked={useAllCategories}
              onChange={() => {
                setUseAllCategories(!useAllCategories);
                setCategories((categories) =>
                  categories.map((category) => ({
                    ...category,
                    checked: !useAllCategories,
                  }))
                );
              }}
            />

            {categories.map((category) => (
              <CheckboxOption
                key={category.id}
                checked={category.checked}
                onChange={() => toggleCategory(category)}
                label={category.name}
                count={category.count}
              />
            ))}
          </Dropdown>

          <div>
            <input
              type="text"
              placeholder="Postcode"
              minLength={4}
              maxLength={4}
              inputMode="numeric"
              value={postalCode}
              onChange={(e) => {
                const { value } = e.target;
                const isPostalCode = postalCodePattern.test(value);

                if (!isPostalCode && sort === 'adjacent') {
                  setSort('recent');
                }

                if (value === '' || /\d{1,4}/.test(value)) {
                  setPostalCode(value);
                }
              }}
            />
          </div>

          <Dropdown
            label={distance > 0 ? `Afstand ${distance} km` : 'Alle afstanden'}
          >
            <RadioOption
              type="radio"
              name="distance"
              value={-1}
              onChange={() => setDistance(-1)}
              checked={-1 === distance}
              disabled={!hasLocation}
            >
              Alle afstanden
            </RadioOption>

            {distances.map((dist) => (
              <RadioOption
                key={dist}
                name="distance"
                value={dist}
                onChange={() => setDistance(dist)}
                disabled={!hasLocation}
                checked={dist === distance}
              >
                Binnen {dist} km
              </RadioOption>
            ))}
          </Dropdown>

          <Button
            type="submit"
            theme="secondary"
            disabled={loading || selectedCategoryCount === 0}
          >
            Zoek
          </Button>

          <label htmlFor="sort_options">Sorteer op</label>
          <SortDropdown
            id="sort_options"
            {...{
              sort,
              categoryIds,
              distance,
              postalCode,
              filterBy,
              keyword,
              hasLocation,
            }}
          />
        </div>
      </form>

      {hasCriteria && (
        <Criteria
          {...{
            adType,
            location,
            filter,
            hasLocation,
            postalCode,
            categoryIds,
            sort,
            filterBy,
            appliedCategories,
            distance,
            hasCriteria,
          }}
        />
      )}

      <Sidebar
        ref={sidebarRef}
        {...{
          isOpen: sidebarOpen,
          categories,
          setCategories,
          totalAdCount,
          useAllCategories,
          setUseAllCategories,
          toggleCategory,
          setDistance,
          setSort,
          setPostalCode,
          hasLocation,
          loading,
          selectedCategoryCount,
          filterBy,
          keyword,
          sort,
          categoryIds,
          distance,
          postalCode,
        }}
      />
    </>
  );
}
